import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ADDED (2026-09-20): CONFIRMED BUG — mustChangePassword and (new)
// needsMfaSetup were only ever enforced by a ONE-TIME navigate() call
// right after login/registration completed. Escaping that single moment
// (closing the tab, a failed ceremony's own "back to SSO" link, an admin
// resetting MFA on an already-signed-in account) left the app fully usable
// with an outstanding requirement forever — confirmed live via a passkey
// setup cancellation that reached the dashboard with zero MFA configured.
//
// This runs on EVERY navigation (see the [user, pathname] effect deps
// below), not just once, and reads mustChangePassword/needsMfaSetup fresh
// off `user` — which accountUpdate.js's /me now recomputes from the
// account's real current state on every call (see that file's own
// comment), not from a stale JWT claim. Same pattern as
// IdleTimeoutWatcher.jsx/ScrollToTop.jsx: a sibling of <Routes>, not
// inside any one page, so it applies regardless of which route is active.
//
// EXEMPT_PATHS: everywhere reachable before a real session exists (login,
// registration, password reset, SSO handoff pages mid-flow) plus the two
// destination pages themselves — redirecting AWAY from those would loop.
const EXEMPT_PATHS = new Set([
  '/', '/callback', '/register', '/sso-sync-callback', '/verify-email',
  '/confirm-email', '/forgot-password', '/reset-password',
  '/claim-account', '/resume-registration',
  '/change-password', '/setup-mfa',
]);

export default function AccountCompletionGuard() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || !user) return;
    if (EXEMPT_PATHS.has(pathname)) return;
    // mustChangePassword takes priority — no point choosing a sign-in
    // method with a password only an admin currently knows.
    if (user.mustChangePassword) {
      navigate('/change-password');
      return;
    }
    if (user.needsMfaSetup) {
      navigate('/setup-mfa');
    }
  }, [user, loading, pathname, navigate]);

  return null;
}
